import {ITippingHistoryRecordViewModel, ITippingHistoryViewModel, ITippingHistoryRecordAmountViewModel} from "./tipping-history-view-model.interface";
import { observable, action, makeObservable } from "mobx";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {GamesMap} from "../../games/games.service.interface";
import {HistoryController_GetTippingHistoryRequest, HistoryController_GetTippingHistoryResponse} from "../../api/api.service";
import {CyClass} from "../../../../common/cy-classes";

export class TippingHistoryBaseModel implements ITippingHistoryViewModel {
    constructor(protected readonly services: IServiceFactoryExtended) {
        makeObservable(this);
    }

    get daysIntervals(): number[] {
        return [30, 60, 90];
    }

    private _numberOfHistoryDays: number = 30;

    @observable.ref
    isLoading: boolean = false;

    @action.bound
    private _setIsLoading(value: boolean): void {
        this.isLoading = value;
    }

    @observable.ref
    records: ITippingHistoryRecordViewModel[] = [];

    @action.bound
    private _setRecords(value: ITippingHistoryRecordViewModel[]): void {
        this.records = value;
    }

    reset() {
        this._setIsLoading(false);
        this._setRecords([]);
    }

    @action.bound
    setNumberOfHistoryDays(numberOfDays: number): void {
        if(numberOfDays === this._numberOfHistoryDays) {
            return;
        }
        this._numberOfHistoryDays = numberOfDays;
        this.reset();
        this.loadAllRecords();
    }

    private async _getGamesInfo(serverResponse: HistoryController_GetTippingHistoryResponse): Promise<GamesMap> {
        const result: GamesMap = {};
        const notInLayoutGames: number[] = [];

        serverResponse.TipHistory.forEach(record => {
            const gameType = record.GameTypeID;
            const gameInfo = this.services.games.gamesMap[gameType];
            if(gameInfo) {
                result[gameInfo.gameType] = gameInfo;
            } else {
                notInLayoutGames.push(gameType);
            }
        });

        if(notInLayoutGames.length > 0) {
            const gamesMap = await this.services.games.getGamesInfoForHistory(notInLayoutGames);
            Object.keys(gamesMap).forEach(gameType => {
                result[gameType] = gamesMap[gameType];
            });
        }

        return result;
    }

    private _getAmounts(recordData: HistoryController_GetTippingHistoryResponse['TipHistory'][0]): ITippingHistoryRecordAmountViewModel[] {
        return [
            {
                description: this.services.translation.translate('TIPPING_HISTORY_AMOUNT'),
                value: this.services.currency.formatMoney(recordData.TipAmount),
                cyClass: CyClass('cy-tipping-history-tip-value')
            },
            {
                description: this.services.translation.translate('CASH_BALANCE'),
                value: this.services.currency.formatMoney(recordData.AvailableBalanceAfterAction),
                cyClass: CyClass('cy-tipping-history-bankroll-value')
            }
        ];
    }


    async loadAllRecords(): Promise<void> {
        if(this.isLoading) {
            return;
        }

        this._setIsLoading(true);

        try {
            const request: HistoryController_GetTippingHistoryRequest = {
                fromLastDays: this._numberOfHistoryDays,
            };
            
            const serverResponse = await this.services.api.historyController.getTippingHistory(request);
            const gamesMap = await this._getGamesInfo(serverResponse);
            const newRecords: ITippingHistoryRecordViewModel[] = [];


            for(let recordData of serverResponse.TipHistory) {
                const gameType = recordData.GameTypeID;
                const gameInfo = gamesMap[gameType];

                if (gameInfo) {
                    newRecords.push({
                        tipId: recordData.TipID?.toString(),
                        gameTypeId: recordData.GameTypeID,
                        gameName: gameInfo.title,
                        gameImage: gameInfo.getGameImage({squareIcon: this.services.theme.shouldUseSquareIcons}),
                        imagePlaceHolderUrl: gameInfo.getGameImagePlaceHolderUrl(this.services.theme.shouldUseSquareIcons),
                        tipDate: recordData.TipDateTime,
                        amounts: this._getAmounts(recordData)
                    });
                } else {
                    this.services.logger.error(`Could not get game info for game type ${gameType} in tipping history!`);
                }
            }

            this._setRecords(newRecords);
        } finally {
            this._setIsLoading(false);
        }
    }
}
